// src/components/home/ProfileMenu.tsx
import React from 'react';
import { FaUser, FaBookmark, FaSignOutAlt, FaStar } from 'react-icons/fa';
import { UserData } from './types';
 
interface ProfileMenuProps {
  darkMode: boolean;
  userData: UserData;
  isOpen: boolean;
  menuRef: React.RefObject<HTMLDivElement>;
  onViewProfile: () => void;
  onLogout: () => void;
  formatCount: (count: number) => string;
}
 
export const ProfileMenu: React.FC<ProfileMenuProps> = ({
  darkMode, userData, isOpen, menuRef,
  onViewProfile, onLogout, formatCount,
}) => {
  if (!isOpen) return null;
 
  const menuBg = darkMode ? '#0f2424' : '#f0fafa';
  const border = darkMode ? 'rgba(90,184,184,0.15)' : 'rgba(13,26,26,0.12)';
  const itemColor = darkMode ? 'rgba(240,250,250,0.7)' : '#0d1a1a';
 
  return (
    <div
      ref={menuRef}
      className="absolute right-0 mt-2 w-72 rounded-xl overflow-hidden z-50"
      style={{
        background: menuBg,
        border: `1.5px solid ${border}`,
        boxShadow: '6px 6px 0 rgba(13,26,26,0.25)',
      }}
    >
      {/* Header band */}
      <div
        className="relative p-5"
        style={{ background: '#0d1a1a', borderBottom: `1.5px solid ${border}` }}
      >
        {/* Ink corner bracket */}
        <span style={{ position: 'absolute', top: 8, left: 8, width: 12, height: 12, borderTop: '2px solid rgba(126,206,206,0.3)', borderLeft: '2px solid rgba(126,206,206,0.3)', display: 'block' }} />
 
        <div className="flex items-start gap-4">
          <div className="relative">
            <img
              src={userData.photoURL}
              alt={userData.username}
              className="w-14 h-14 rounded-full"
              style={{ border: '2px solid rgba(90,184,184,0.4)' }}
            />
            {userData.isPremium && (
              <div className="absolute -bottom-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center"
                style={{ background: '#e8a820', border: '2px solid #0d1a1a' }}>
                <FaStar style={{ color: '#0d1a1a', fontSize: '8px' }} />
              </div>
            )}
          </div>
          <div className="flex-1">
            <h3 className="font-black text-base" style={{ color: '#f0fafa', letterSpacing: '0.5px' }}>
              {userData.username}
            </h3>
            <p className="text-xs font-bold mt-0.5" style={{ color: 'rgba(126,206,206,0.5)' }}>
              {userData.email}
            </p>
            <div className="mt-2 flex gap-2">
              <span
                className="text-xs font-black px-2 py-1 rounded"
                style={{ background: 'rgba(90,184,184,0.15)', color: '#7ecece', letterSpacing: '1px' }}
              >
                LVL {userData.level}
              </span>
              {userData.isPremium && (
                <span className="text-xs font-black px-2 py-1 rounded"
                  style={{ background: '#e8a820', color: '#0d1a1a', letterSpacing: '1px' }}>
                  PRO
                </span>
              )}
            </div>
          </div>
        </div>
      </div>
 
      {/* Stats row */}
      <div
        className="grid grid-cols-3 gap-2 p-4 border-b"
        style={{ borderColor: border }}
      >
        {[
          { label: 'POSTS', value: userData.posts },
          { label: 'FOLLOWERS', value: formatCount(userData.followers) },
          { label: 'FOLLOWING', value: formatCount(userData.following) },
        ].map(s => (
          <div key={s.label} className="text-center">
            <div className="text-lg font-black" style={{ color: darkMode ? '#f0fafa' : '#0d1a1a' }}>{s.value}</div>
            <div className="text-[9px] font-bold mt-0.5" style={{ color: darkMode ? 'rgba(126,206,206,0.4)' : 'rgba(13,26,26,0.45)', letterSpacing: '0.5px' }}>
              {s.label}
            </div>
          </div>
        ))}
      </div>
 
      {/* Menu items */}
      <div className="p-2">
        {[
          { icon: FaUser, label: 'VIEW PROFILE', action: onViewProfile },
          { icon: FaBookmark, label: 'SAVED POSTS', action: () => {} },
        ].map(item => (
          <button
            key={item.label}
            onClick={item.action}
            className="w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all text-sm font-bold text-left"
            style={{ color: itemColor, letterSpacing: '0.5px' }}
            onMouseEnter={e => (e.currentTarget.style.background = darkMode ? 'rgba(90,184,184,0.08)' : 'rgba(13,26,26,0.05)')}
            onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
          >
            <item.icon style={{ fontSize: '14px' }} />
            <span>{item.label}</span>
          </button>
        ))}
      </div>
 
      <div className="p-2 border-t" style={{ borderColor: border }}>
        <button
          onClick={onLogout}
          className="w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all text-sm font-bold"
          style={{ color: '#e05050', letterSpacing: '0.5px' }}
          onMouseEnter={e => (e.currentTarget.style.background = 'rgba(224,80,80,0.08)')}
          onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
        >
          <FaSignOutAlt style={{ fontSize: '14px' }} />
          <span>LOGOUT</span>
        </button>
      </div>
    </div>
  );
};